import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DkNavService } from '../../../services/dk-nav.service';
import { MenuDto } from '../../../models/ortak.dtos';
import { Observable, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DxTileViewModule, DxTextBoxModule, DxButtonModule } from 'devextreme-angular';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { menuIkonCoz, yedekRenk } from '../dk-side-navigation-menu/menu-gorunum';

/** Penceredeki bir modül kartı: ikon/renk MENU_ICON_URL'den, başlık aranan kısım vurgulu. */
interface ModulKarti extends MenuDto {
  ikonSinif: string;
  renk: string;
  parcalar: { metin: string; eslesme: boolean }[];
}

interface PencereGorunumu {
  arama: string;
  tumu?: MenuDto;
  moduller: ModulKarti[];
}

@Component({
  selector: 'dk-modules',
  standalone: true,
  imports: [CommonModule, DxTileViewModule, DxTextBoxModule, DxButtonModule, ReactiveFormsModule],
  templateUrl: './dk-modules.component.html',
  styleUrls: ['./dk-modules.component.scss'],
})
export class DkModulesComponent implements OnInit {
  @Output() closePopover: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('aramaKutusu') aramaKutusu?: ElementRef<HTMLInputElement>;
  modulelist$: Observable<MenuDto[]>;
  filteredModulelist$: Observable<MenuDto[]>;
  gorunum$: Observable<PencereGorunumu>;
  searchControl = new FormControl('');

  constructor(private dkNavServices: DkNavService) {
    this.modulelist$ = dkNavServices.navigationx$;

    this.filteredModulelist$ = combineLatest([
      this.modulelist$,
      this.searchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([modules, searchTerm]) => {
        if (!searchTerm || searchTerm.trim() === '') {
          return modules;
        }
        const term = searchTerm.toLowerCase().trim();
        return modules.filter(module =>
          module.menuTitle?.toLowerCase().includes(term)
        );
      })
    );

    // Kabuk canlandırma (02.10.2026): "Tüm Uygulamalar" (moduleId 0) bantta düğme olur, kalanlar düz liste.
    this.gorunum$ = combineLatest([
      this.modulelist$,
      this.searchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([modules, searchTerm]) => {
        const arama = (searchTerm ?? '').trim();
        const q = arama.toLocaleLowerCase('tr');
        const liste = modules ?? [];
        return {
          arama,
          tumu: liste.find((m) => !m.moduleId),
          moduller: liste
            .filter((m) => m.moduleId && (!q || m.menuTitle?.toLocaleLowerCase('tr').includes(q)))
            .map((m) => this.kart(m, q)),
        };
      })
    );
  }

  /** Yan menünün gösterdiği modül ("Açık" rozeti). */
  get aktifModulId(): number {
    return this.dkNavServices.aktifModulId;
  }

  ngOnInit() {
    this.dkNavServices.getModules();
  }

  onItemClick(data: any){
    this.dkNavServices.getMenu(data.moduleId);
    this.closePopover.emit(data);
  }

  onSearchValueChanged(e: any) {
    this.searchControl.setValue(e.value || '', { emitEvent: true });
  }

  onSearch() {
    // Arama zaten reactive olarak çalışıyor, bu metod Enter tuşu ve buton tıklaması için
    this.searchControl.setValue(this.searchControl.value || '', { emitEvent: true });
  }

  /** Pencere açılınca arama kutusuna odaklanır (yan menü popover onShown'dan çağırır). */
  odakla() {
    setTimeout(() => this.aramaKutusu?.nativeElement.focus());
  }

  ilkSonucuAc(moduller: ModulKarti[]) {
    if (moduller.length) this.onItemClick(moduller[0]);
  }

  aramayiTemizle() {
    this.searchControl.setValue('', { emitEvent: true });
  }

  modulKodu(_: number, m: ModulKarti) {
    return m.moduleId;
  }

  private kart(m: MenuDto, q: string): ModulKarti {
    const gorunum = menuIkonCoz(m.icon);
    const ikon = gorunum.ikon || 'inactivefolder';
    return {
      ...m,
      ikonSinif: ikon.includes('fa-') ? ikon : `dx-icon-${ikon}`,
      renk: gorunum.renk ?? yedekRenk(m.moduleId),
      parcalar: this.vurgula(m.menuTitle ?? '', q),
    } as ModulKarti;
  }

  private vurgula(baslik: string, q: string): { metin: string; eslesme: boolean }[] {
    const i = q ? baslik.toLocaleLowerCase('tr').indexOf(q) : -1;
    if (i < 0) return [{ metin: baslik, eslesme: false }];
    return [
      { metin: baslik.slice(0, i), eslesme: false },
      { metin: baslik.slice(i, i + q.length), eslesme: true },
      { metin: baslik.slice(i + q.length), eslesme: false },
    ].filter((p) => p.metin);
  }
}
